import React, { useState, useCallback, useRef } from 'react';
import './App.css';

/* ───── Types ───── */
interface TimingInfo {
  authTimeMs?: number;
  authCached?: boolean;
  requestTimeMs?: number;
  parseTimeMs?: number;
  extractTimeMs?: number;
  totalTimeMs?: number;
  rawResponseSizeKb?: number;
}

interface OcrPanelData {
  processed?: any;
  raw?: any;
  error?: string;
  processingTimeMs?: number;
  authTimeMs?: number;
  authCached?: boolean;
  requestTimeMs?: number;
  parseTimeMs?: number;
  extractTimeMs?: number;
  totalTimeMs?: number;
  rawResponseSizeKb?: number;
}

interface OcrResult {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  fileBlob: string; // base64 for download
  documentAi: OcrPanelData;
  deapOcr: OcrPanelData;
  timestamp: Date;
}

interface QueueItem {
  id: string;
  file: File;
  status: 'waiting' | 'processing' | 'done' | 'cancelled';
  progress: number;
}

/* ───── Helpers ───── */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function formatTime(ms?: number): string {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatDate(date: Date): string {
  return date.toLocaleString('cs-CZ', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function syntaxHighlight(obj: any): string {
  const json = JSON.stringify(obj, null, 2);
  if (!json) return '';
  return json.replace(
    /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    (match) => {
      let cls = 'jt-number';  // numbers
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'jt-key';      // keys (property names)
        } else {
          cls = 'jt-string';   // string values
        }
      } else if (/true|false/.test(match)) {
        cls = 'jt-boolean';
      } else if (/null/.test(match)) {
        cls = 'jt-null';
      }
      return `<span class="${cls}">${escapeHtml(match)}</span>`;
    }
  );
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

function downloadJSON(data: any, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadBase64(base64: string, filename: string, mimeType: string) {
  const byteChars = atob(base64);
  const byteArr = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArr], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ───── App ───── */
function App() {
  const [results, setResults] = useState<OcrResult[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [orderId, setOrderId] = useState('638fbbd0-9d19-4136-8055-a256ebf48ce9');
  const [orderHash, setOrderHash] = useState('a2e7610873de3699e36b2bc404680c4a516cb154b904c590e63d2f843e4c2608');
  const processingRef = useRef(false);
  const cancelledRef = useRef<Set<string>>(new Set());

  const toggleCard = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const removeResult = (id: string) => {
    setResults(prev => prev.filter(r => r.id !== id));
    setExpandedCards(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const clearAll = () => {
    setResults([]);
    setExpandedCards(new Set());
  };

  const cancelQueueItem = (id: string) => {
    cancelledRef.current.add(id);
    setQueue(prev => prev.map(q => q.id === id ? { ...q, status: 'cancelled' } : q));
  };

  const processFiles = useCallback(async (files: File[]) => {
    const newItems: QueueItem[] = files.map(f => ({
      id: uid(),
      file: f,
      status: 'waiting' as const,
      progress: 0,
    }));

    setQueue(prev => [...prev, ...newItems]);

    // If already processing, the loop will pick them up
    if (processingRef.current) return;
    processingRef.current = true;

    const processNext = async () => {
      setQueue(currentQueue => {
        // We need to work with the latest queue
        return currentQueue;
      });

      // Use a helper to get current queue state
      let items: QueueItem[] = [];
      setQueue(prev => { items = prev; return prev; });

      // Wait a tick to ensure state is ready
      await new Promise(r => setTimeout(r, 50));
      setQueue(prev => { items = prev; return prev; });

      const next = items.find(q => q.status === 'waiting');
      if (!next) {
        processingRef.current = false;
        // Clean done/cancelled items from queue after a delay
        setTimeout(() => {
          setQueue(prev => prev.filter(q => q.status !== 'done' && q.status !== 'cancelled'));
        }, 1000);
        return;
      }

      if (cancelledRef.current.has(next.id)) {
        cancelledRef.current.delete(next.id);
        await processNext();
        return;
      }

      // Mark as processing
      setQueue(prev => prev.map(q => q.id === next.id ? { ...q, status: 'processing', progress: 10 } : q));

      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setQueue(prev => prev.map(q =>
            q.id === next.id && q.status === 'processing'
              ? { ...q, progress: Math.min(q.progress + 5, 90) }
              : q
          ));
        }, 500);

        const formData = new FormData();
        formData.append('file', next.file);
        formData.append('orderId', orderId);
        formData.append('orderHash', orderHash);

        const response = await fetch('/api/ocr/compare', {
          method: 'POST',
          body: formData,
        });

        clearInterval(progressInterval);

        if (cancelledRef.current.has(next.id)) {
          cancelledRef.current.delete(next.id);
          setQueue(prev => prev.map(q => q.id === next.id ? { ...q, status: 'cancelled', progress: 0 } : q));
          await processNext();
          return;
        }

        if (!response.ok) throw new Error(`Server error: ${response.status}`);
        const data = await response.json();

        // Convert file to base64 for download
        const fileBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.readAsDataURL(next.file);
        });

        const result: OcrResult = {
          id: next.id,
          fileName: data.fileName,
          fileSize: data.fileSize,
          mimeType: data.mimeType,
          fileBlob: fileBase64,
          documentAi: data.documentAi,
          deapOcr: data.deapOcr,
          timestamp: new Date(),
        };

        setResults(prev => [result, ...prev]);
        setExpandedCards(prev => new Set(prev).add(result.id));
        setQueue(prev => prev.map(q => q.id === next.id ? { ...q, status: 'done', progress: 100 } : q));
      } catch (err: any) {
        setQueue(prev => prev.map(q => q.id === next.id ? { ...q, status: 'done', progress: 100 } : q));
        const result: OcrResult = {
          id: next.id,
          fileName: next.file.name,
          fileSize: next.file.size,
          mimeType: next.file.type,
          fileBlob: '',
          documentAi: { error: err.message },
          deapOcr: { error: err.message },
          timestamp: new Date(),
        };
        setResults(prev => [result, ...prev]);
        setExpandedCards(prev => new Set(prev).add(result.id));
      }

      await processNext();
    };

    // Kick off after state update
    setTimeout(() => processNext(), 100);
  }, []);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    processFiles(Array.from(fileList));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [processFiles]);

  const activeQueue = queue.filter(q => q.status === 'waiting' || q.status === 'processing');

  return (
    <>
      {/* Header - full width */}
      <header className="header">
        <div className="header-inner">
          <div className="header-title">
            <h1>OCR Comparator</h1>
            <span className="header-subtitle">Porovnání GCP Document AI vs DEAP OCR</span>
          </div>
          {results.length > 0 && (
            <button className="btn-clear-all" onClick={clearAll}>
              Clear All Results
            </button>
          )}
        </div>
      </header>

    <div className="app">
      {/* Body */}
      <div className="body-wrap">
        {/* DEAP OCR Config */}
        <div className="config-row">
          <div className="config-field">
            <label className="config-label">Order ID</label>
            <input
              className="config-input"
              type="text"
              value={orderId}
              onChange={e => setOrderId(e.target.value)}
              placeholder="orderId"
            />
          </div>
          <div className="config-field">
            <label className="config-label">Order Hash</label>
            <input
              className="config-input"
              type="text"
              value={orderHash}
              onChange={e => setOrderHash(e.target.value)}
              placeholder="orderHash"
            />
          </div>
        </div>

        {/* Upload Zone */}
        <div
          className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.tiff"
            multiple
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            hidden
          />
          <svg className="upload-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#58A6FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
            <path d="M12 12v9" />
            <path d="m16 16-4-4-4 4" />
          </svg>
          <span className="upload-title">Drag & drop PDF files here, or click to browse</span>
          <span className="upload-helper">Supports PDF files up to 50 MB each</span>
        </div>

        {/* Processing Queue */}
        {activeQueue.length > 0 && (
          <div className="queue-box">
            {activeQueue.map((item, idx) => (
              <div
                key={item.id}
                className={`queue-row ${idx === 0 ? 'first' : ''} ${idx === activeQueue.length - 1 ? 'last' : ''}`}
              >
                <span className="queue-txt">
                  {item.status === 'processing' ? `Processing: ${item.file.name}` : `Queued: ${item.file.name}`}
                </span>
                <div className="queue-right">
                  {item.status === 'processing' ? (
                    <div className="progress-bg">
                      <div className="progress-fill" style={{ width: `${item.progress}%` }} />
                    </div>
                  ) : (
                    <span className="queue-waiting">Waiting...</span>
                  )}
                  <button className="queue-cancel" onClick={(e) => { e.stopPropagation(); cancelQueueItem(item.id); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F85149" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Result Cards */}
        <div className="results-area">
          {results.map(result => (
            <ResultCard
              key={result.id}
              result={result}
              expanded={expandedCards.has(result.id)}
              onToggle={() => toggleCard(result.id)}
              onRemove={() => removeResult(result.id)}
            />
          ))}
        </div>
      </div>
    </div>
    </>
  );
}

/* ───── Result Card (Accordion) ───── */
function ResultCard({
  result, expanded, onToggle, onRemove,
}: {
  result: OcrResult;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const ext = result.fileName.split('.').pop()?.toUpperCase() || 'FILE';

  return (
    <div className="result-card">
      {/* Card Header */}
      <div className="card-header" onClick={onToggle}>
        <div className="card-header-left">
          <svg
            className={`chevron ${expanded ? 'open' : ''}`}
            width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="#484F58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
          <div className="card-file-info">
            <span className="card-filename">{result.fileName}</span>
            <span className="card-badge">{ext}</span>
            <span className="card-meta">
              {formatBytes(result.fileSize)}  ·  {formatDate(result.timestamp)}
            </span>
          </div>
        </div>
        <div className="card-actions" onClick={e => e.stopPropagation()}>
          {result.fileBlob && (
            <button
              className="btn-ghost"
              onClick={() => downloadBase64(result.fileBlob, result.fileName, result.mimeType)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#30363D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Download</span>
            </button>
          )}
          <button className="btn-trash" onClick={onRemove}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F85149" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expandable Content */}
      {expanded && (
        <div className="card-panels">
          <OcrPanel
            title="GCP Document AI"
            subtitle="Zpracovaný výstup (extrahované entity)"
            data={result.documentAi.error ? { error: result.documentAi.error } : result.documentAi.processed}
            rawData={result.documentAi.raw}
            error={result.documentAi.error}
            timings={[
              { label: result.documentAi.authCached ? 'Auth (cache)' : 'Auth', value: result.documentAi.authTimeMs, variant: result.documentAi.authCached ? 'auth-cached' : 'auth' },
              { label: 'Request', value: result.documentAi.requestTimeMs, variant: 'request' },
              { label: 'Parse', value: result.documentAi.parseTimeMs, variant: 'parse' },
              { label: 'Total', value: result.documentAi.totalTimeMs, variant: 'total' },
            ]}
            fileName={result.fileName}
            serviceName="documentai"
          />
          <OcrPanel
            title="DEAP OCR"
            subtitle="Surový výstup z API"
            data={result.deapOcr.error ? { error: result.deapOcr.error } : result.deapOcr.raw}
            rawData={result.deapOcr.raw}
            error={result.deapOcr.error}
            timings={[
              { label: 'Total', value: result.deapOcr.processingTimeMs, variant: 'total' },
            ]}
            fileName={result.fileName}
            serviceName="deapocr"
          />
        </div>
      )}
    </div>
  );
}

/* ───── OCR Panel ───── */
function OcrPanel({
  title, subtitle, data, rawData, error, timings, fileName, serviceName,
}: {
  title: string;
  subtitle: string;
  data: any;
  rawData: any;
  error?: string;
  timings: { label: string; value?: number; variant?: string }[];
  fileName: string;
  serviceName: string;
}) {
  const getChipClass = (t: { value?: number; variant?: string }) => {
    const classes = ['timing-chip'];
    if (t.variant) classes.push(t.variant);
    // Mark as slow if request/total > 5s
    if (t.value && t.value > 5000 && t.variant !== 'auth' && t.variant !== 'auth-cached' && t.variant !== 'parse') {
      classes.push('slow');
    }
    return classes.join(' ');
  };

  return (
    <div className="ocr-panel">
      {/* Panel Head */}
      <div className="panel-head">
        <div className="panel-name-group">
          <span className="panel-name">{title}</span>
          <span className="panel-subtitle">{subtitle}</span>
        </div>
        <span className={`panel-status-badge ${error ? 'error' : 'success'}`}>
          {error ? 'Error' : 'Success'}
        </span>
      </div>

      {/* Timing Badges */}
      <div className="timing-badges">
        {timings.map(t => (
          <div key={t.label} className={getChipClass(t)}>
            <span className="timing-label">{t.label}</span>
            <span className="timing-value">{formatTime(t.value)}</span>
          </div>
        ))}
      </div>

      {/* JSON Preview */}
      <span className="json-label">Response Preview</span>
      <pre className={`json-box ${error ? 'json-error' : ''}`}
        dangerouslySetInnerHTML={{ __html: error ? escapeHtml(JSON.stringify(data, null, 2)) : syntaxHighlight(data) }}
      />

      {/* Raw Response Download */}
      {rawData && (
        <button
          className="btn-ghost"
          onClick={() => downloadJSON(rawData, `${fileName}_${serviceName}_raw.json`)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#30363D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>Raw response</span>
        </button>
      )}
    </div>
  );
}

export default App;
